import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { AdminOverrideBadgeComponent } from '@features/shopping/admin-override/admin-override-badge/admin-override-badge.component';
import { GlobalDeviceDetailsComponent } from '@features/shopping/device-details-global/global-device-details.component';
import { UserAsset } from '@models/dashboard.model';
import { CartInfo } from '@models/session-details';
import { InCartObjects, ShoppingCartItem } from '@models/shopping-cart.model';
import {
  CART_ITEM_TYPE_ACCESSORY,
  CART_ITEM_TYPE_DEVICE,
  CART_ITEM_TYPE_FEATURE,
  CART_ITEM_TYPE_PLAN,
  ColorOption,
  ColorsListResponse,
  DeviceItemDevice,
  DeviceItemDeviceBP,
  FeatureType,
  NameOverrideListResponse,
  StorageListResponse
} from '@models/shopping.model';
import { TranslationPipe } from '@pipes/translation/translation.pipe';
import { ActivityStateService } from '@services/activity/state/activity-state.service';
import { ShoppingService } from '@services/shopping/shopping.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'ts-device-details-options',
  templateUrl: './device-details-options.component.html',
  imports: [CommonModule, TranslationPipe, AdminOverrideBadgeComponent]
})
export class DeviceDetailsOptionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) global: GlobalDeviceDetailsComponent;
  @Input() carrierChanged$?: EventEmitter<void>;
  @Input() load_color_list: boolean = false;
  @Input() load_storage_list_post_check: boolean = false;
  @Input() set_name_override_id: boolean = false;
  @Input() forward_backward_logic: boolean = false;
  @Input() reset_list_options: boolean = false;
  @Output() options_disable_btn: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() storage_list_length: EventEmitter<boolean> = new EventEmitter<boolean>();
  color_list: ColorOption[] = [];
  storage_list: DeviceItemDevice[] = [];
  name_override_list: DeviceItemDevice[] = [];
  show_options: boolean = false;
  device_selection_device_id: number | undefined = undefined;

  private destroy$ = new Subject<void>();

  constructor(
    private activity_state_svc: ActivityStateService,
    private shopping_svc: ShoppingService,
    public cdr: ChangeDetectorRef
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    // Subscribe to carrier change event for Order Type 3
    if (this.current_activity_id === 3 && this.carrierChanged$) {
      this.carrierChanged$.pipe(takeUntil(this.destroy$)).subscribe(() => this.onCarrierChanged());
    }
    // Color cascade is initiated solely by the load_color_list input, set by selectCarrier
    // after loadCarrierList resolves. Do not call loadColorList here to avoid a duplicate
    // API call racing with the authoritative trigger.
  }

  /**
   * Order Type 3: Handle carrier change event from GlobalDeviceDetailsComponent
   * Reloads name overrides for the new carrier and selected device
   */
  onCarrierChanged(): void {
    if (![3].includes(this.current_activity_id)) return;

    // Ensure device info is ready before loading
    if (!this.global.active_device?.id) return;

    // Reset state
    this.name_override_list = [];
    this.show_options = false;
    this.global.selected_name_override_id = -1;

    // Mark as not revisiting to ensure plans/features reload
    this.global.revisiting = false;

    // Reload from color list to ensure correct flow
    this.loadColorList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle input changes - check if property exists in changes object
    if (changes['load_color_list'] && changes['load_color_list'].currentValue) {
      this.loadColorList();
    }
    if (changes['load_storage_list_post_check']?.currentValue) {
      this.loadStorageListPostCheck();
    }
    if (changes['set_name_override_id']?.currentValue) {
      this.global.selected_name_override_id = this.global.in_cart_device?.id ?? -1;
    }
    if (changes['forward_backward_logic']?.currentValue) {
      this.forwardBackwardLinkingLogic();
    }
    if (changes['reset_list_options']?.currentValue) {
      this.resetListOptions();
    }
  }

  get current_activity_id(): number {
    return this.global.current_activity_id;
  }

  get selected_storage_device_price() {
    return this.global.selected_storage_device_price;
  }

  private resetListOptions(): void {
    this.color_list = [];
    this.storage_list = [];
    this.name_override_list = [];
    this.show_options = false;
  }

  private forwardBackwardLinkingLogic(): void {
    const cart_objects: InCartObjects = this.global['retrieveCartObjects']();

    // For Order Type 3: Check if there are cart items to restore
    const has_cart_items =
      cart_objects.plan_id || (cart_objects.features && cart_objects.features.length > 0);

    // Skip forward/backward linking for Order Type 3 if:
    // 1. Name override is already selected AND
    // 2. No cart items to restore (meaning this is auto-select, not genuine revisit)
    if (
      [3].includes(this.current_activity_id) &&
      this.global.selected_name_override_id !== -1 &&
      !has_cart_items
    ) {
      return;
    }

    if (![3].includes(this.current_activity_id)) {
      const target =
        [...this.name_override_list, ...this.storage_list].find((device: DeviceItemDevice) =>
          [this.global.selected_name_override_id, this.global.selected_storage_device_id].includes(
            device.id
          )
        ) ?? this.global.in_cart_device;
      if (target) this.selectStorage(target, true);
    } else {
      this.global.selected_storage_size = this.global.selected_device_size;
      this.global.selected_name_override_id = this.global.in_cart_device?.id as number;
      this.global.selected_plan_id = cart_objects.plan_id;
    }

    if (
      !this.global.keep_plan &&
      (!!this.name_override_list.find(
        (device: DeviceItemDevice) => device.id == this.global.selected_name_override_id
      ) ||
        this.name_override_list.length === 1)
    ) {
      this.global['loadDevicePlanData'](
        this.global.selected_storage_device_id ||
          cart_objects.device_id ||
          this.global.selected_device_id,
        () => {
          const feature_ids_to_restore: Array<string | number> =
            cart_objects.features?.map((f: FeatureType) => f.id) ??
            this.global.shopping_cart.shopping_cart_items
              .filter((item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_FEATURE)
              .map((item: ShoppingCartItem) => item.id);

          this.global['loadFeatureList'](
            cart_objects.plan_id ?? this.global.selected_plan_id,
            feature_ids_to_restore
          );
          this.global.selected_plan_id = cart_objects.plan_id;
          this.global.selected_feature_list = cart_objects.features ?? [];
        }
      );
    }
  }

  private loadColorList(): void {
    this.shopping_svc
      .getColorsList(
        {
          ...this.global.devices_payload,
          business_purpose: this.global.is_bp_device
            ? (this.global.active_device as DeviceItemDeviceBP)?.business_purpose
            : undefined
        },
        this.global.selected_carrier_id
      )
      .subscribe({
        next: (res: ColorsListResponse) => {
          const bp_color: string =
            this.global.device_record?.[0]?.devices?.[0]?.color?.toLowerCase().trim() ?? '';
          const bp_colors: ColorOption[] = res.items.filter(
            (color: ColorOption) => color.color?.toLowerCase().trim() === bp_color
          );

          this.color_list = this.global.is_bp_device && bp_colors.length ? bp_colors : res.items;

          // selected_normalized_color is sourced from cart items, backend device records and
          // (on OT3/BP) the URL filter param, so its casing and padding are not guaranteed to
          // match the catalog. Normalize both sides, as loadDevice already does when matching
          // the active device, so a case difference cannot fall through to the color fallbacks.
          const normalized_color: string =
            this.global.selected_normalized_color?.toLowerCase().trim() ?? '';

          const preferredColor: ColorOption | undefined =
            this.color_list.find(
              (color: ColorOption) => color.id === this.global.selected_color_device_id
            ) ??
            this.color_list.find(
              (color: ColorOption) =>
                !!normalized_color &&
                (color.original_color?.toLowerCase().trim() === normalized_color ||
                  color.color?.toLowerCase().trim() === normalized_color)
            ) ??
            this.color_list.find(
              (color: ColorOption) => color.original_color?.toLowerCase() === 'black'
            ) ??
            this.color_list[0];

          if (preferredColor) this.selectColor(preferredColor as unknown as DeviceItemDevice, true);
        },
        error: (e: HttpErrorResponse) => {}
      });
  }

  private selectColor(device_details: DeviceItemDevice, preserve_selection: boolean = false): void {
    // Skip only if same device AND not preserving selection (user clicking same color again)
    if (this.global.selected_color_device_id === device_details.id && !preserve_selection) return;

    // For BP devices, the color list may return items annotated with a different BP
    // (e.g. WM01's device record appearing in WM03's color list because both BPs share
    // the same physical device). Patch the correct BP from device_record so all downstream
    // cascade calls (storage list, name overrides, cart item) use the right business purpose.
    if (this.global.is_bp_device && this.global.device_record?.length) {
      const correct_bp: string = (this.global.device_record[0]?.devices?.[0] as DeviceItemDeviceBP)
        ?.business_purpose;
      if (correct_bp)
        device_details = { ...device_details, business_purpose: correct_bp } as DeviceItemDevice;
    }

    this.global.active_device = device_details;
    this.name_override_list = [];
    if (!preserve_selection) {
      // Remove old device from cart when changing colors
      this.global['removeItems'](CART_ITEM_TYPE_DEVICE);
      this.global.selected_storage_device_id = -1;
      this.global.selected_storage_size = '';
      this.global.selected_name_override_id = -1;
      this.show_options = false;
      // Clear stale plan/feature state so the new color starts fresh
      if (!this.global.keep_plan) {
        this.global.plan_list = this.global.feature_list = this.global.selected_feature_list = [];
        this.global.selected_plan_id = 0;
        this.global.no_plans = this.global.no_features = false;
        this.global['removeItems'](CART_ITEM_TYPE_PLAN);
        this.global['removeItems'](CART_ITEM_TYPE_FEATURE);
        this.global.plan_features_sub.next();
      }
    }
    this.global.selected_storage_device_price = {
      onetime: { cost_to_currency: '' },
      monthly: { cost_to_currency: '' }
    };

    // if (this.keep_plan && !preserve_selection) this.keepPlansAndFeatures();

    // Always update these to ensure UI state is correct (especially when preserve_selection=true during revisit)
    this.global.selected_device_id = this.global.active_device.id;
    this.global.selected_normalized_color = (this.global.active_device as DeviceItemDevice).color;
    this.global.selected_color_device_id = device_details.id;
    this.global.is_display_one_time_cost =
      this.global.selected_storage_device_id !== -1 ||
      !!this.global.shopping_cart.shopping_cart_items.find(
        (item: ShoppingCartItem) => item.type === CART_ITEM_TYPE_ACCESSORY
      );
    if (!preserve_selection) this.global.refresh_summary.next(true);
    this.global['updateQueryParams']();
    this.loadStorageList(device_details);
  }

  private loadStorageList(device_details: DeviceItemDevice): void {
    this.shopping_svc
      .getStorageList(
        {
          ...this.global.devices_payload,
          business_purpose: this.global.is_bp_device
            ? (this.global.active_device as DeviceItemDeviceBP).business_purpose
            : undefined
        },
        this.global.selected_carrier_id,
        this.global.selected_normalized_color
      )
      .subscribe({
        next: (res: StorageListResponse) => {
          this.storage_list = res.items;
          this.storage_list_length.emit(this.storage_list.length > 0);
          if ([1001].includes(this.current_activity_id)) {
            if (this.global.revisiting)
              this.global.shopping_cart.shopping_cart_items.forEach(
                (cart_item: ShoppingCartItem, index: number) => {
                  if (cart_item.type === CART_ITEM_TYPE_DEVICE && cart_item.additional_info) {
                    cart_item.additional_info.business_purpose = this.global.is_bp_device
                      ? (this.global.active_device as DeviceItemDeviceBP).business_purpose
                      : undefined;
                    this.global.shopping_cart.shopping_cart_items[index] = cart_item;
                  }
                }
              );
          }

          if (this.global.in_cart_device) {
            const matched_storage: DeviceItemDevice | undefined = this.storage_list.find(
              (device: DeviceItemDevice) => {
                return (
                  (device.size === this.global.in_cart_device?.size &&
                    device.price.onetime.cost_to_currency ===
                      this.global.in_cart_device?.price.onetime.cost_to_currency &&
                    (this.global.in_cart_device?.price.monthly
                      ? this.global.in_cart_device.price.monthly.cost_to_currency ===
                        device.price.monthly?.cost_to_currency
                      : true)) ||
                  device.id === this.global.in_cart_device?.id
                );
              }
            );
            if (matched_storage) {
              this.global.storage_auto_select_needed = false;
              this.global.storage_reselect_needed = false;
              this.global.selected_storage_device_id = matched_storage.id;
              this.global.selected_device_id = this.global.in_cart_device.id;
              this.global.selected_name_override_id = this.global.in_cart_device.id;
              this.selectStorage(matched_storage, true);
              return;
            }
          }

          this.loadStorageListPostCheck();
        },
        error: (e: HttpErrorResponse) => {}
      });
  }

  private loadStorageListPostCheck(): void {
    const device_in_local_cart: ShoppingCartItem | undefined =
      this.global.getItemByType(CART_ITEM_TYPE_DEVICE);
    const should_auto_select: boolean =
      this.storage_list.length > 0 &&
      ((this.storage_list.length === 1 &&
        (!device_in_local_cart || this.global.selected_storage_device_id === -1)) ||
        (!device_in_local_cart && this.global.storage_auto_select_needed));
    const has_storage_selection: boolean =
      this.global.selected_storage_device_id !== -1 || !!this.global.selected_storage_size;
    const reselect_allowed_for_activity: boolean =
      ![3].includes(this.current_activity_id) || !!device_in_local_cart;
    const should_reselect: boolean =
      this.global.storage_reselect_needed &&
      reselect_allowed_for_activity &&
      this.storage_list.length > 0 &&
      has_storage_selection;

    this.global.storage_reselect_needed = false;

    if (should_reselect) {
      const retained_storage: DeviceItemDevice | undefined =
        this.storage_list.find(
          (device: DeviceItemDevice) => device.id === this.global.selected_storage_device_id
        ) ??
        this.storage_list.find(
          (device: DeviceItemDevice) => device.size === this.global.selected_storage_size
        );
      this.selectStorage(retained_storage ?? this.storage_list[0], !!retained_storage);
    } else if (should_auto_select) {
      const auto_select_target: DeviceItemDevice = this.global.storage_auto_select_needed
        ? this.storage_list[0]
        : (this.global.in_cart_device ?? this.storage_list[0]);
      this.global.storage_auto_select_needed = false;
      this.shopping_svc.preselected_item = true;
      this.selectStorage(auto_select_target);
    } else if (
      [3].includes(this.current_activity_id) &&
      this.storage_list.length > 0 &&
      !this.global.getItemByType(CART_ITEM_TYPE_DEVICE)
    ) {
      if (this.global.revisiting) {
        const target_storage: DeviceItemDevice =
          this.storage_list.find((device: DeviceItemDevice) => {
            return (
              device.id === this.global.selected_storage_device_id ||
              device.size === this.global.selected_storage_size
            );
          }) ?? this.storage_list[0];
        this.selectStorage(target_storage, true);
      } else {
        const fresh_storage: DeviceItemDevice =
          this.storage_list.find(
            (device: DeviceItemDevice) => device.size === this.global.selected_device_size
          ) ?? this.storage_list[0];
        this.selectStorage(fresh_storage);
      }
    }
  }

  private selectStorage(
    device_details: DeviceItemDevice,
    preserve_selection: boolean = false
  ): void {
    // If the same storage is re-clicked and already fully loaded, preserve all selections
    const is_same_storage: boolean = device_details.id === this.global.selected_storage_device_id;
    if (!preserve_selection && is_same_storage && this.name_override_list.length > 0) return;

    // For Order Type 3: If name override is already selected, preserve it
    const already_selected_override: boolean =
      [3].includes(this.current_activity_id) &&
      this.global.selected_name_override_id !== -1 &&
      this.name_override_list.length > 0;

    this.activity_state_svc.setSteps(this.global.progress_bar_config);

    if (
      !preserve_selection &&
      !this.shopping_svc.preselected_item &&
      !is_same_storage &&
      this.global.selected_storage_device_id !== -1
    )
      this.global['resetPlansAndFeatures']();

    // Consume unconditionally, even when the reset is skipped for other reasons, so a stale
    // value can never suppress a later user-initiated storage change.
    const preserve_for_toggle: boolean = this.global.preserve_plan_selection_on_reload;
    this.global.preserve_plan_selection_on_reload = false;

    // resetPlanSelection discards the plan, the features, the cart items and the CartInfo copy.
    // That is correct when the user picks a different storage, but an admin-toggle reload only
    // looks like one — skipping it there is what keeps end-user selections across the toggle.
    if (!this.global.keep_plan && !is_same_storage && !preserve_for_toggle)
      this.global['resetPlanSelection'](false);

    this.global.selected_storage_device_id = device_details.id;
    this.selected_storage_device_price.onetime = device_details.price.onetime;
    this.selected_storage_device_price.monthly = device_details.price.monthly;

    if (this.global.keep_plan) this.global['keepPlansAndFeatures']();

    this.global.selected_storage_size = device_details.size;
    this.global.is_display_one_time_cost = true;
    this.global.is_display_recurring_cost = true;

    // Ensure color is synchronized with the device being selected
    this.global.selected_normalized_color = device_details.color;

    this.name_override_list = [];
    if (!preserve_selection && !already_selected_override) {
      this.global.selected_name_override_id = -1;
      this.show_options = false;
    }

    // Skip reloading name overrides if already selected for Order Type 3
    if (!already_selected_override) {
      // Add device to local pricing cart now so one-time cost is visible immediately,
      // without waiting for the user to select a name override option.
      // selectNameOverride will remove and re-add with the correct override device later.
      this.global['removeItems'](CART_ITEM_TYPE_DEVICE);
      this.global['generateShoppingItems'](
        device_details,
        CART_ITEM_TYPE_DEVICE,
        true,
        this.global.devices_payload.categories[0].id
      );
      this.loadNameOverrides(device_details);
    }
  }

  private loadNameOverrides(device_details: DeviceItemDevice): void {
    // For Order Type 3: active_device holds the COLOR-level variant (e.g. the highest storage
    // size), which differs from the storage variant selectStorage actually selected. Trust the
    // selected storage device (the storage_list entry matching selected_storage_device_id) so the
    // name-override query and URL keep the chosen variant; only fall back to active_device when no
    // storage entry matches (preserves the carrier-change case where device_details may be stale).
    const device_to_use: DeviceItemDevice =
      [3].includes(this.current_activity_id) && this.global.active_device
        ? (this.storage_list.find(
            (device: DeviceItemDevice) => device.id === this.global.selected_storage_device_id
          ) ?? this.global.active_device)
        : device_details;

    if (!device_to_use.id) return;

    this.shopping_svc
      .getNameOverridesList(
        {
          ...this.global.devices_payload,
          business_purpose: this.global.is_bp_device
            ? (this.global.active_device as DeviceItemDeviceBP).business_purpose
            : undefined,
          onetime_price_in_cents: device_to_use.price.onetime.fractional
        },
        this.global.selected_carrier_id,
        this.global.selected_normalized_color,
        this.global.selected_storage_size
      )
      .subscribe({
        next: (res: NameOverrideListResponse) => {
          const restore_id: number = this.global.name_override_restore_id;
          this.global.name_override_restore_id = -1;

          this.name_override_list = res.items.filter(
            (item: DeviceItemDevice) =>
              !device_to_use.price.monthly ||
              item.price.monthly?.cost_to_currency === device_to_use.price.monthly.cost_to_currency
          );

          const selection_in_list: boolean = this.name_override_list.some(
            (override: DeviceItemDevice) => override.id === this.global.selected_name_override_id
          );

          if (
            !selection_in_list &&
            this.global.revisiting &&
            this.name_override_list.some(
              (override: DeviceItemDevice) => override.id === device_to_use.id
            )
          )
            this.global.selected_name_override_id = device_to_use.id;

          this.show_options =
            this.name_override_list.length > 1 ||
            (this.name_override_list.length > 0 &&
              this.name_override_list.every((device: DeviceItemDevice) => device.name_override));

          this.options_disable_btn.emit(this.show_options && this.name_override_list.length > 0);

          if (this.show_options) {
            this.name_override_list.forEach((device: DeviceItemDevice) => {
              if (!device.name_override) device.name_override = device.name;
            });
          }

          const preferred_override: DeviceItemDevice | undefined = this.name_override_list.find(
            (device: DeviceItemDevice) => device.id === this.global.selected_name_override_id
          );

          const nonAccessoryCartItems: ShoppingCartItem[] = [
            ...(CartInfo.cart_data?.shopping_cart_items ?? []),
            ...this.global.shopping_cart.shopping_cart_items
          ].filter((item: ShoppingCartItem) => item.type !== CART_ITEM_TYPE_ACCESSORY);

          // OT3 backward navigation: revisiting is intentionally false (carrier-change guard
          // in loadDevice), but if the user has a plan in cart and the plan list is not yet
          // loaded, treat it as a revisit so the preferred override is auto-selected and
          // plans/features are restored. On carrier change selected_name_override_id is reset
          // to -1, so preferred_override is undefined and this path is never entered.
          const ot3_plan_restore_needed: boolean =
            [3].includes(this.current_activity_id) &&
            this.global.selected_plan_id !== 0 &&
            this.global.plan_list.length === 0;

          if (
            preferred_override &&
            (this.global.revisiting || ot3_plan_restore_needed) &&
            nonAccessoryCartItems.length > 0
          ) {
            this.shopping_svc.preselected_item = false;
            this.selectNameOverride(preferred_override);
            return;
          }

          // Case 1: No name overrides - proceed directly to load plans/features
          if (this.name_override_list.length === 0) {
            if (!this.global.keep_plan) {
              // No name overrides: Set device info and update query params
              this.global.active_device = device_to_use;
              this.global.selected_device_id = device_to_use.id;
              this.global.selected_name_override_id = device_to_use.id;

              // Update query params with correct deviceId
              this.global['updateQueryParams']();

              // For order type 3 when revisiting, pass feature IDs to restore
              if ([3].includes(this.current_activity_id) && this.global.revisiting) {
                const cart_objects: InCartObjects =
                  this.global['retrieveCartObjects'](device_to_use);
                const feature_ids_to_restore: Array<string | number> =
                  cart_objects.features?.map((f: any) => String(f.id)) ?? [];
                this.global['loadDevicePlanData'](device_to_use.id, () => {
                  this.global['loadFeatureList'](
                    cart_objects.plan_id ?? this.global.selected_plan_id,
                    feature_ids_to_restore
                  );
                  this.global.selected_plan_id = cart_objects.plan_id;
                  this.global.selected_feature_list = cart_objects.features ?? [];
                });
              } else {
                this.global['loadDevicePlanData'](device_to_use.id);
              }
            }

            // Rebuild the device cart item so pricing is recalculated even when
            // the selected device ID stays the same after admin-toggle reloads.
            const existing_device = this.global.shopping_cart.shopping_cart_items.find(
              (cart_item: ShoppingCartItem) => cart_item.type === CART_ITEM_TYPE_DEVICE
            );

            if (existing_device) {
              this.global['removeItems'](CART_ITEM_TYPE_DEVICE);
            }

            this.global['generateShoppingItems'](
              device_to_use,
              CART_ITEM_TYPE_DEVICE,
              true,
              this.global.devices_payload.categories[0].id
            );
          }
          // Case 2: Single name override - auto-select it
          else if (this.name_override_list.length === 1) {
            this.selectNameOverride(this.name_override_list[0]);
          } else if (
            restore_id !== -1 &&
            !this.global.keep_plan &&
            ![1001].includes(this.activity_state_svc.orderTypeId())
          ) {
            const restore_target: DeviceItemDevice | undefined = this.name_override_list.find(
              (device: DeviceItemDevice) => device.id === restore_id
            );
            if (restore_target) this.selectNameOverride(restore_target);
            else if (
              [3].includes(this.current_activity_id) &&
              this.global.shopping_cart.device_include_admin_options === true
            )
              this.global['loadDevicePlanData'](device_to_use.id);
          }

          if (
            [1001].includes(this.activity_state_svc.orderTypeId()) &&
            this.global.selected_name_override_id !== -1
          )
            this.global['loadDevicePlanData'](device_to_use.id);

          this.shopping_svc.preselected_item = false;
        },
        error: (e: HttpErrorResponse) => {}
      });
  }

  private selectNameOverride(device_details: DeviceItemDevice): void {
    // OT3 backward nav: plan exists in cart but plan list is empty — must restore.
    // Same condition as in loadNameOverrides so both guards are consistent.
    const ot3_plan_restore_needed: boolean =
      [3].includes(this.current_activity_id) &&
      this.global.selected_plan_id !== 0 &&
      this.global.plan_list.length === 0;

    // Skip only when the same override is re-selected, we are not revisiting, no OT3 restore
    // is pending, and the plan list is already populated (nothing left to load).
    if (
      this.global.selected_name_override_id === device_details.id &&
      !this.global.revisiting &&
      !ot3_plan_restore_needed &&
      this.global.plan_list.length > 0
    )
      return;

    const is_name_override_change: boolean =
      this.global.selected_name_override_id !== -1 &&
      this.global.selected_name_override_id !== device_details.id;

    this.global.active_device = device_details;
    this.global.selected_device_id = device_details.id;
    this.global.selected_name_override_id = device_details.id;

    // Update query params with selected device ID
    this.global['updateQueryParams']();

    this.activity_state_svc.setSteps(this.global.progress_bar_config);

    if (this.global.revisiting || ot3_plan_restore_needed) {
      const cart_objects: InCartObjects = this.global['retrieveCartObjects'](device_details);
      const feature_ids_to_restore: Array<string | number> =
        cart_objects.features?.map((f: FeatureType) => f.id) ?? [];

      // For Order Type 3: If no cart items to restore, load fresh plans
      if (
        [3].includes(this.current_activity_id) &&
        !cart_objects.plan_id &&
        feature_ids_to_restore.length === 0
      ) {
        this.global['loadDevicePlanData'](
          this.global.selected_storage_device_id > 0
            ? this.global.selected_storage_device_id
            : device_details.id
        );
      } else {
        // Standard revisit flow - restore cart items
        this.global['loadDevicePlanData'](
          (this.global.selected_storage_device_id > 0 && this.global.selected_storage_device_id) ||
            cart_objects.device_id ||
            this.global.selected_device_id,
          () => {
            this.global['loadFeatureList'](
              cart_objects.plan_id ?? this.global.selected_plan_id,
              feature_ids_to_restore
            );
            this.global.selected_plan_id = cart_objects.plan_id;
            this.global.selected_feature_list = cart_objects.features ?? [];
          }
        );
      }
    } else if (
      !this.global.keep_plan &&
      (!this.global.admin_override_persist || is_name_override_change)
    ) {
      this.global.selected_plan_id = 0;
      this.global.feature_list = this.global.selected_feature_list = [];
      this.global['removeItems'](CART_ITEM_TYPE_PLAN);
      this.global['removeItems'](CART_ITEM_TYPE_FEATURE);
      this.global['loadDevicePlanData'](
        (this.global.selected_storage_device_id > 0 && this.global.selected_storage_device_id) ||
          device_details.id
      );
    } else if (device_details.admin || this.global.admin_override_persist) {
      const cart_objects: InCartObjects = this.global['retrieveCartObjects'](device_details);
      const feature_ids_to_restore: Array<string | number> =
        cart_objects.features?.map((f: FeatureType) => f.id) ?? [];

      this.global['loadDevicePlanData'](
        (this.global.selected_storage_device_id > 0 && this.global.selected_storage_device_id) ||
          cart_objects.device_id ||
          this.global.selected_device_id,
        () => {
          this.global['loadFeatureList'](
            cart_objects.plan_id ?? this.global.selected_plan_id,
            feature_ids_to_restore
          );
          this.global.selected_plan_id = cart_objects.plan_id;
          this.global.selected_feature_list = cart_objects.features ?? [];
        }
      );
    }

    this.global['removeItems'](CART_ITEM_TYPE_DEVICE);

    this.global.device_plan.deviceId = this.global.selected_name_override_id = device_details.id;
    this.global.selected_device_id = device_details.id;
    this.global.is_display_one_time_cost = true;

    // Update query params with selected name override deviceId
    this.global['updateQueryParams']();

    if (this.show_options && this.global.shopping_cart.existing_asset) {
      if (!this.device_selection_device_id && this.global.shopping_cart.existing_asset)
        this.device_selection_device_id = this.global.shopping_cart.existing_asset.device.id;

      this.global.shopping_cart.existing_asset = {
        device: {
          existing_asset_id: this.device_selection_device_id,
          id: device_details.id,
          manufacturer: device_details.manufacturer,
          model: device_details.model,
          color: device_details.color,
          type: device_details.product_category,
          imei: this.global.shopping_cart.existing_asset?.device.imei,
          device_catalog_id: device_details.device_catalog_id,
          size: device_details.size,
          serial_number: this.global.shopping_cart.existing_asset?.device.serial_number,
          tech_specs: {
            sim_compatibility:
              this.global.shopping_cart.existing_asset?.device.tech_specs.sim_compatibility
          },
          image_url: device_details.image_url,
          normalized_color: device_details.original_color,
          is_override: true
        },
        asset_from_inventory: this.global.shopping_cart.existing_asset.asset_from_inventory,
        asset_selection_case: this.global.shopping_cart.existing_asset.asset_selection_case,
        device_identifier_search_value:
          this.global.shopping_cart.existing_asset?.device_identifier_search_value
      } as UserAsset;
    }

    this.global['generateShoppingItems'](
      device_details,
      CART_ITEM_TYPE_DEVICE,
      true,
      this.global.devices_payload.categories[0].id
    );
  }

  selectColorClick(device_details: DeviceItemDevice): void {
    if (
      this.global.revisiting &&
      device_details.id != this.global.selected_color_device_id &&
      !this.global.modal_dismissed
    ) {
      this.global['confirmChangesModal'](() => this.selectColor(device_details));
    } else this.selectColor(device_details);
  }

  selectStorageClick(device_details: DeviceItemDevice): void {
    if (
      this.global.revisiting &&
      (device_details.size != this.global.selected_storage_size ||
        device_details.id != this.global.selected_device_id) &&
      this.global.selected_storage_size !== '' &&
      !this.global.modal_dismissed
    ) {
      this.global['confirmChangesModal'](() => this.selectStorage(device_details));
    } else this.selectStorage(device_details);
  }

  selectNameOverrideClick(device_details: DeviceItemDevice): void {
    if (
      this.global.revisiting &&
      device_details.id !== this.global.selected_name_override_id &&
      this.global.selected_name_override_id !== -1 &&
      !this.global.modal_dismissed
    ) {
      this.global['confirmChangesModal'](() => this.selectNameOverride(device_details));
    } else this.selectNameOverride(device_details);
  }

  appendMonthlyLabel(cost: string): string {
    return this.global.appendMonthlyLabel(cost);
  }

  getActiveStorage = (device: DeviceItemDevice): boolean => {
    if (device.id === this.global.selected_storage_device_id) return true;

    const oneTimeMatch: boolean =
      device.price.onetime.cost_to_currency ===
      this.selected_storage_device_price.onetime.cost_to_currency;
    const monthlyMatch: boolean = device.price.monthly
      ? device.price.monthly?.cost_to_currency ===
        this.selected_storage_device_price.monthly?.cost_to_currency
      : true;

    return device.size === this.global.selected_storage_size && oneTimeMatch && monthlyMatch;
  };
}
